/**
 * @fileoverview Configuration boundary for the Secresea co-signing module.
 *
 * Everything host-specific (API base, relay base, where the WASM is served,
 * which `fetch` to use, network) is injected here rather than hardcoded, so
 * this whole `cosign/` directory can later be lifted out into a standalone,
 * installable package ("add MPC co-signing to your Zcash site") with no
 * Secresea coupling. Per project conventions we never export mutable
 * objects/arrays or `let` bindings — config is passed by value through
 * function parameters; `resolveCosignConfig()` returns a fresh frozen object.
 *
 * The defaults assume the common single-origin deployment (Secresea's own
 * frontend + API on the same origin), so a host that mounts the NFPT API at
 * `/api` and serves the WASM from `/orchard-frost` needs to pass nothing.
 */

import type { OrchardFrostWasmModule } from './orchardFrost';

/** Zcash network selector understood by the PCZT endpoints. */
export type ZcashNetwork = 'main' | 'test';

export interface CosignConfig {
	/**
	 * Base path/URL for the PCZT endpoints
	 * (`/build`, `/orchard-sighash`, `/apply-orchard-frost`, `/broadcast`).
	 */
	pcztApiBaseUrl: string;
	/**
	 * Base path/URL for the WB32COSIGN relay
	 * (`POST /:sessionId`, `POST /message/:sessionId`, `GET /message/:sessionId/:partyId`).
	 */
	relayBaseUrl: string;
	/** Base path/URL under which the orchard-frost WASM assets are served. */
	wasmBaseUrl: string;
	/** Zcash network for builds/proofs. */
	network: ZcashNetwork;
	/** `fetch` implementation (injectable for tests / non-browser hosts). */
	fetchImpl: typeof fetch;
	/**
	 * Optional override for loading the Orchard FROST WASM. Hosts that bundle
	 * the WASM with their own toolchain provide this; otherwise the default
	 * loader fetches the artefacts from `wasmBaseUrl`.
	 */
	loadOrchardFrostWasm?: () => Promise<OrchardFrostWasmModule>;
	/** Optional cache-bust token appended to WASM asset URLs (e.g. a build id). */
	assetCacheBustToken?: string;
}

// Named defaults — single source of truth, easy to retune without hunting
// through call sites.
export const DEFAULT_PCZT_API_BASE_URL = '/api/pczt';
export const DEFAULT_RELAY_BASE_URL = '/api/cosign';
export const DEFAULT_WASM_BASE_URL = '/orchard-frost';
export const DEFAULT_NETWORK: ZcashNetwork = 'main';

const stripTrailingSlash = (s: string): string => s.replace(/\/+$/, '');

const defaultFetch = (): typeof fetch => {
	const f = (typeof globalThis !== 'undefined' ? (globalThis as any).fetch : undefined) as typeof fetch | undefined;
	if (typeof f !== 'function') {
		throw new Error('No fetch implementation available; pass `fetchImpl` in the cosign config.');
	}
	return f.bind(globalThis);
};

/**
 * Merge caller overrides with the defaults and return a fresh, frozen config.
 *
 * @param partial Any subset of {@link CosignConfig}; omitted fields fall back
 *   to the module defaults.
 */
export const resolveCosignConfig = (partial: Partial<CosignConfig> = {}): Readonly<CosignConfig> => {
	const config: CosignConfig = {
		pcztApiBaseUrl: stripTrailingSlash(partial.pcztApiBaseUrl ?? DEFAULT_PCZT_API_BASE_URL),
		relayBaseUrl: stripTrailingSlash(partial.relayBaseUrl ?? DEFAULT_RELAY_BASE_URL),
		wasmBaseUrl: stripTrailingSlash(partial.wasmBaseUrl ?? DEFAULT_WASM_BASE_URL),
		network: partial.network ?? DEFAULT_NETWORK,
		fetchImpl: partial.fetchImpl ?? defaultFetch(),
		loadOrchardFrostWasm: partial.loadOrchardFrostWasm,
		assetCacheBustToken: partial.assetCacheBustToken,
	};
	return Object.freeze(config);
};
